/** Build / sync a draft revenue invoice from an accepted local proposal. */

import { createRevenueInvoice, patchRevenueInvoice } from "./revenueApi";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Line items for the accepted option (+ optional add-on). */
export function lineItemsFromAcceptedProposal(packet) {
  if (!packet || packet.status !== "accepted") return [];
  const opts = packet.options || [];
  const idx =
    packet.acceptedOptionIndex != null && opts[packet.acceptedOptionIndex]
      ? packet.acceptedOptionIndex
      : 0;
  const opt = opts[idx];
  if (!opt) return [];

  const mainAmount = roundMoney(opt.investment_total ?? opt.total ?? opt.subtotal ?? 0);
  const lines = [
    {
      title: (opt.option_label || packet.acceptedOptionLabel || "Proposal").trim() || "Proposal",
      description: String(opt.intro || "").trim(),
      amount: mainAmount > 0 ? mainAmount : 0,
    },
  ];

  const addon = packet.acceptedAddon;
  if (addon) {
    const addonAmount = roundMoney(addon.amount);
    if (addonAmount > 0 || (addon.title || "").trim()) {
      lines.push({
        title: String(addon.title || "Add-on").trim() || "Add-on",
        description: String(addon.description || "").trim(),
        amount: addonAmount > 0 ? addonAmount : 0,
      });
    }
  }

  return lines.filter((l) => l.amount > 0);
}

/**
 * Ensure a draft invoice exists with the accepted option + selected add-on as line items.
 * Call from studio (admin) context — e.g. prospect record after accept.
 */
export async function ensureInvoiceFromAcceptedProposal(packet, client) {
  if (!packet || packet.status !== "accepted" || !client?.id) return null;
  const lineItems = lineItemsFromAcceptedProposal(packet);
  if (!lineItems.length) return null;
  const amount = roundMoney(lineItems.reduce((sum, row) => sum + (Number(row.amount) || 0), 0));
  if (amount <= 0) return null;

  const issued = todayIso();
  const serviceTitle = lineItems[0].title;
  const serviceDescription = lineItems[0].description || undefined;

  if (packet.revenueInvoiceId) {
    try {
      const patched = await patchRevenueInvoice(packet.revenueInvoiceId, {
        line_items: lineItems,
      });
      return patched?.invoice || patched;
    } catch {
      // Fall through and create a fresh draft.
    }
  }

  return createRevenueInvoice({
    client_id: client.id,
    amount,
    issued,
    due: issued,
    service_tier: client.service_tier || "operational_ecosystem",
    invoice_service_title: serviceTitle,
    invoice_service_description: serviceDescription,
    payment_plan: "full",
    line_items: lineItems,
  });
}
